import { useEffect, useMemo, useRef } from "react"
import { ModalWrapper } from "../../templates/ModalWrapper"
import styles from "./styles.module.scss"
import { observer } from "mobx-react-lite"
import { makeAutoObservable } from "mobx"
import Carousel from "react-multi-carousel"
import { ErrorHandledImage } from "../../atoms/ErrorHandledImage"
import { isMobile } from "react-device-detect"

class ImagePreviewModalStore {
  aspectRatio: number | null
  currentImageIndex: number
  zoomPosition: { top: number }

  constructor(initialIndex?: number) {
    this.aspectRatio = null
    this.currentImageIndex = initialIndex || 0
    this.zoomPosition = { top: 50 }
    makeAutoObservable(this)
  }

  setAspectRatio(value: number | null) {
    this.aspectRatio = value
  }

  setCurrentImageIndex(value: number) {
    this.currentImageIndex = value
  }

  setZoomPosition(value: { top: number }) {
    this.zoomPosition = value
  }
}

export const ImagePreviewModal = observer(
  ({
    files,
    onClose,
    initialIndex,
    isDeckMap = false,
  }: {
    files: {
      fileName: string
      fileUrl: string
    }[]
    onClose: (index?: number) => void
    initialIndex?: number
    isDeckMap?: boolean
  }) => {
    const state = useMemo(() => new ImagePreviewModalStore(initialIndex), [initialIndex])

    const isCarousel = files?.length > 1 && initialIndex !== undefined

    const carouselRef = useRef<Carousel>(null)

    useEffect(() => {
      if (isCarousel) {
        carouselRef?.current?.goToSlide(state.currentImageIndex)
      }
    }, [])

    useEffect(() => {
      const img = new Image()
      img.src = files[state.currentImageIndex].fileUrl

      img.onload = () => {
        const width = img.width
        const height = img.height
        const ratio = width / height

        state.setAspectRatio(ratio)
      }
    }, [state.currentImageIndex])

    const responsive = {
      desktop: {
        breakpoint: { max: 3000, min: 0 },
        items: 1,
      },
    }

    const handleMouseMove = (e) => {
      const { top, height } = e.target.getBoundingClientRect()
      const mouseY = e.clientY - top
      // position as a percentage (adjust by 12.5 so top and bottom overshoot the same amount)
      const zoomY = (mouseY / height) * 100 - 12.5
      state.setZoomPosition({ top: zoomY })
    }

    return (
      <ModalWrapper onClose={() => onClose(state.currentImageIndex)}>
        {isCarousel ? (
          <div
            className={styles.carouselContainer}
            style={
              {
                "--aspect-ratio": state.aspectRatio,
                "--zoom-position": `${-state.zoomPosition.top}%`,
              } as React.CSSProperties
            }
          >
            <Carousel
              ref={carouselRef}
              swipeable={false}
              draggable={false}
              responsive={responsive}
              keyBoardControl={false}
              transitionDuration={500}
              afterChange={(_, { currentSlide }) => state.setCurrentImageIndex(currentSlide)}
            >
              {isDeckMap
                ? files.map((file, index) => {
                    if (isMobile) {
                      return (
                        <div className={styles.zoomImageContainer} key={index}>
                          <div className={styles.fullImageContainer}>
                            <ErrorHandledImage
                              uri={file.fileUrl}
                              alt={file.fileName}
                              className={styles.fullImage}
                            />
                          </div>
                        </div>
                      )
                    }

                    return (
                      <div className={styles.zoomImageContainer} key={index}>
                        <div className={styles.fullImageContainer}>
                          <p>{file.fileName}</p>
                          <ErrorHandledImage
                            uri={file.fileUrl}
                            alt={file.fileName}
                            className={styles.fullImage}
                            onMouseMove={handleMouseMove}
                            onMouseLeave={() => state.setZoomPosition({ top: 50 })}
                          />
                        </div>

                        <div className={styles.zoomContainer}>
                          <ErrorHandledImage
                            uri={file.fileUrl}
                            alt={file.fileName}
                            className={styles.zoomedImage}
                          />
                        </div>
                      </div>
                    )
                  })
                : files.map((file, index) => {
                    return (
                      <div
                        className={styles.imageContainer}
                        style={{
                          aspectRatio: state.aspectRatio ? `${state.aspectRatio}` : "auto",
                          width: "100%",
                        }}
                        key={index}
                      >
                        <ErrorHandledImage
                          uri={file.fileUrl}
                          alt={file.fileName}
                          className={styles.imagePreview}
                        />
                      </div>
                    )
                  })}
            </Carousel>
          </div>
        ) : (
          <div
            className={styles.imageContainer}
            style={{
              aspectRatio: state.aspectRatio ? `${state.aspectRatio}` : "auto",
              width: "100%",
            }}
          >
            <ErrorHandledImage
              uri={files[state.currentImageIndex].fileUrl}
              alt={files[state.currentImageIndex].fileName}
              className={styles.imagePreview}
            />
          </div>
        )}
      </ModalWrapper>
    )
  },
)
